import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { motion, useAnimation } from 'framer-motion';

import { WidthContainer, PaddingContainer } from '@components';
import { howWeWorkData } from '@store/UIUXData';
import { colors } from '@styles/core-styles/theme';
import { ArrElement } from '@utils/types';

import * as styles from './HowItWorks.module.scss';

const iconAnimationVariants = {
	active: {
		backgroundColor: [colors.white, colors.orange],
	},
};

const typographyAnimationVariants = {
	active: {
		color: [colors.black, colors.orange],
	},
};

const imageAnimationVariants = {
	fadeIn: {
		opacity: 1,
	},
	fadeOut: {
		opacity: 0,
	},
};

export const HowItWorks: React.FC = () => {
	const [currentItem, setCurrentItem] = useState(howWeWorkData[0]);
	const animationIntervalRef = useRef(null);
	const imageAnimationControls = useAnimation();

	const handleFeatureChange = async (
		item: ArrElement<typeof howWeWorkData>
	) => {
		if (item.title === currentItem.title) return;
		await imageAnimationControls.start('fadeOut');
		setCurrentItem(item);
		if (animationIntervalRef.current) {
			clearInterval(animationIntervalRef.current);
		}
	};

	const handleLoad = async () => {
		await imageAnimationControls.start('fadeIn');
	};

	useEffect(() => {
		const interval = setInterval(() => {
			imageAnimationControls.start('fadeOut');
			setCurrentItem((item) => {
				const index = howWeWorkData.findIndex((i) => i.title === item.title);

				if (index === howWeWorkData.length - 1) {
					return howWeWorkData[0];
				}
				return howWeWorkData[index + 1];
			});
		}, 2500);
		animationIntervalRef.current = interval;

		return () => clearInterval(interval);
	}, []);

	return (
		<PaddingContainer className="white-bg">
			<WidthContainer>
				<div>
					<h2 className="font-size-medium-title">How does it work?</h2>
				</div>
				<div
					className={classNames(
						styles.container,
						'd-flex flex-column flex-xl-row mt-5'
					)}
				>
					<motion.div
						animate={imageAnimationControls}
						className={classNames(
							styles.imageContainer,
							'd-flex justify-content-center align-items-center'
						)}
						variants={imageAnimationVariants}
						transition={{
							duration: 0.2,
						}}
					>
						<img
							onLoad={handleLoad}
							src={currentItem.image}
							alt={currentItem.title}
						/>
					</motion.div>
					<div className={classNames(styles.typographyContainer, 'relative')}>
						<div className={styles.straightLine} />
						<ul className="d-flex flex-column relative">
							{howWeWorkData.map((item) => {
								const isActive = currentItem.title === item.title;
								return (
									<motion.li
										onClick={() => handleFeatureChange(item)}
										key={item.title}
										className="d-flex"
										animate={isActive ? 'active' : 'blur'}
									>
										<motion.div
											variants={iconAnimationVariants}
											transition={{ duration: 0.2 }}
											className={styles.icon}
										>
											<item.Icon
												color={isActive ? colors.white : colors.orange}
											/>
										</motion.div>
										<div className={styles.typography}>
											<motion.h6
												variants={typographyAnimationVariants}
												transition={{ duration: 0.2 }}
												className="font-size-md"
											>
												{item.title}
											</motion.h6>
											<p>{item.description}</p>
										</div>
									</motion.li>
								);
							})}
						</ul>
					</div>
				</div>
			</WidthContainer>
		</PaddingContainer>
	);
};
